import React, { useState, useEffect, useRef, useMemo } from 'react';
import FormGroup from '../bootstrap/forms/FormGroup';
import Input from '../bootstrap/forms/Input';
import Icon from '../icon/Icon';
import Spinner from '../bootstrap/Spinner';
import { getAPI } from '../../apis/backend/apiNode';

const FicheSearchDropdown = ({ 
  value,           // selected fiche object or null
  onChange,        // callback when fiche is selected (receives fiche object)
  onClear,         // optional callback when selection is cleared
  placeholder = "Rechercher une fiche...",
  disabled = false,
  required = false,
  label = "Fiche",
  id = "fiche-search"
}) => {
  const [allFiches, setAllFiches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Load all fiches on mount
  useEffect(() => {
    const loadFiches = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getAPI('fiche', '/all?limit=2000&sortBy=last_updated');
        if (!res?.success) throw new Error(res?.message || 'Failed to load fiches');
        setAllFiches(res.data || []);
      } catch (err) {
        setError(err.message || 'Unable to load fiches');
      } finally {
        setLoading(false);
      }
    };
    loadFiches();
  }, []);

  // Update search query when value changes externally
  useEffect(() => {
    if (value?.name) {
      setSearchQuery(value.name);
    } else if (!value) {
      setSearchQuery('');
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Filter fiches based on search query
  const filteredFiches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allFiches.slice(0, 50);
    return allFiches
      .filter(
        (f) =>
          f.name?.toLowerCase().includes(q) ||
          f.place_id?.toLowerCase().includes(q) ||
          f.formatted_address?.toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [searchQuery, allFiches]);

  const handleSelect = (fiche) => {
    onChange(fiche);
    setSearchQuery(fiche.name);
    setDropdownOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setDropdownOpen(true);
    if (onClear) onClear();
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    // Clear selected fiche if user types something different
    if (value && newValue !== value.name) {
      onChange(null);
    }
    setDropdownOpen(true);
  };

  return (
    <FormGroup label={label} id={id} error={error || (required && !value && 'Please select a fiche')}>
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        {/* Input with dropdown icon */}
        <div style={{ position: 'relative' }}>
          <Input
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={() => setDropdownOpen(true)}
            placeholder={loading ? 'Loading fiches...' : placeholder}
            disabled={disabled || loading}
            autoComplete="off"
          />
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: 'var(--color-text-secondary)',
            }}>
            {loading ? <Spinner isSmall /> : <Icon icon="ChevronDown" style={{ fontSize: 14 }} />}
          </span>
        </div>

        {/* Dropdown menu */}
        {dropdownOpen && !loading && (
          <div
            className="border rounded"
            style={{
              position: 'absolute',
              zIndex: 1050,
              width: '100%',
              background: '#fff',
              maxHeight: 260,
              overflowY: 'auto',
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              marginTop: 4,
            }}>
            {error ? (
              <div
                style={{
                  padding: '10px 14px',
                  fontSize: 13,
                  color: 'var(--color-text-danger)',
                }}>
                <Icon icon="AlertCircle" style={{ marginRight: 8 }} />
                {error}
              </div>
            ) : filteredFiches.length === 0 ? (
              <div
                style={{
                  padding: '10px 14px',
                  fontSize: 13,
                  color: 'var(--color-text-secondary)',
                }}>
                No fiches found
              </div>
            ) : (
              <>
                {filteredFiches.map((fiche) => {
                  const isSelected = value?._id === fiche._id;
                  return (
                    <div
                      key={fiche._id || fiche.place_id}
                      onMouseDown={() => handleSelect(fiche)}
                      style={{
                        padding: '8px 14px',
                        cursor: 'pointer',
                        borderBottom: '0.5px solid var(--color-border-tertiary)',
                        background: isSelected ? 'var(--color-background-info)' : 'transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background = 'var(--color-background-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = 'transparent';
                      }}>
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: 13,
                          color: isSelected
                            ? 'var(--color-text-info)'
                            : 'var(--color-text-primary)',
                        }}>
                        {fiche.name}
                      </span>
                      {(fiche.formatted_address || fiche.place_id) && (
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--color-text-secondary)',
                          }}>
                          {fiche.formatted_address || fiche.place_id}
                        </span>
                      )}
                    </div>
                  );
                })}
                {filteredFiches.length === 50 && (
                  <div
                    style={{
                      padding: '6px 14px',
                      fontSize: 11,
                      color: 'var(--color-text-secondary)',
                      borderTop: '0.5px solid var(--color-border-tertiary)',
                      fontStyle: 'italic',
                    }}>
                    Showing first 50 results — refine your search
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Selected fiche badge */}
      {value && (
        <div
          className="d-flex align-items-center gap-2 mt-2 p-2 rounded"
          style={{
            background: 'var(--color-background-success)',
            fontSize: 13,
          }}>
          <Icon icon="CircleCheck" style={{ color: 'var(--color-text-success)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ color: 'var(--color-text-success)', fontWeight: 500 }}>
              {value.name}
            </span>
            {value.place_id && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 11,
                  color: 'var(--color-text-success)',
                  opacity: 0.75,
                }}>
                · {value.place_id}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 4px',
              color: 'var(--color-text-success)',
              opacity: 0.7,
              fontSize: 16,
              lineHeight: 1,
            }}
            title="Change fiche">
            ✕
          </button>
        </div>
      )}
    </FormGroup>
  );
};

export default FicheSearchDropdown;